<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required, email, url } from '@vuelidate/validators'

export interface ContactFormProps {
  name?: string
}

withDefaults(defineProps<ContactFormProps>(), {
  name: 'JobApplication'
})

const emit = defineEmits(['submit'])
const isSending = ref(false)
const route = useRoute()

const state = reactive({
  name: '',
  email: '',
  portfolio: '',
  jobTitle: route.params.slug
})

const rules = {
  name: { required },
  email: { required, email },
  portfolio: { required, url }
}

const v$ = useVuelidate(rules, state)

// Form submission
async function submitForm($event: Event) {
  const isFormInvalid = await v$.value.$validate()
  if (!isFormInvalid) {
    return
  }

  const formData = new FormData($event.target as HTMLFormElement)

  try {
    isSending.value = true
    await $fetch('/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams(formData).toString()
    })

    emit('submit')
  } catch (e) {
    console.error(e)
  } finally {
    isSending.value = false
  }
}
</script>

<template>
  <form
    id="job-application"
    class="grid grid-cols-2 gap-4"
    :name="name"
    data-netlify="true"
    data-netlify-honeypot="bot-field"
    novalidate="true"
    @submit.prevent="submitForm"
  >
    <input type="hidden" name="form-name" :value="name" />
    <input id="JobTitle" name="JobTitle" type="hidden" :value="state.jobTitle" />
    <div class="col-span-full lg:col-span-1">
      <FormInput
        v-model="state.name"
        label="Name"
        name="name"
        :is-required="true"
        :is-error="v$.name.$error"
        :message="v$.$errors.length > 0 && v$.name.$errors[0]?.$message"
        :is-message-visible="v$.name.$error"
        :message-fixed="true"
        @change="v$.name.$touch"
      />
    </div>
    <div class="col-span-full lg:col-span-1">
      <FormInput
        v-model="state.email"
        type="email"
        label="Email"
        name="email"
        :is-required="true"
        :is-error="v$.email.$error"
        :message="v$.$errors.length > 0 && v$.email.$errors[0]?.$message"
        :is-message-visible="v$.email.$error"
        :message-fixed="true"
        @change="v$.email.$touch"
      />
    </div>
    <div class="col-span-full">
      <FormInput
        v-model="state.portfolio"
        label="Portfolio Link"
        name="portfolio"
        :is-required="true"
        :is-error="v$.portfolio.$error"
        :message="v$.$errors.length > 0 && v$.portfolio.$errors[0]?.$message"
        :is-message-visible="v$.portfolio.$error"
        :message-fixed="true"
        @change="v$.portfolio.$touch"
      />
    </div>
    <div class="col-span-full mt-4 lg:mt-8 lg:text-right">
      <Button
        type="submit"
        size="lg"
        variant="cta"
        label="Send"
        class="w-full md:w-fit lg:min-w-40"
      />
    </div>
  </form>
</template>
